import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';

export interface ShortcutDefinition {
  id: string;
  label: string;
  description: string;
  defaultKey: string;
  category: string;
  adminOnly?: boolean;
  action?: string;
  path?: string;
}

export const DEFAULT_SHORTCUTS: ShortcutDefinition[] = [
  { id: 'dashboard', label: 'Tableau de bord', description: 'Accéder au tableau de bord', defaultKey: 'Alt+D', category: 'Navigation', path: '/dashboard' },
  { id: 'mon-espace', label: 'Mon Espace', description: 'Accéder à mon espace personnel', defaultKey: 'Alt+H', category: 'Navigation', path: '/mon-espace' },
  { id: 'dossiers', label: 'Gestion des Dossiers', description: 'Accéder à la gestion des dossiers', defaultKey: 'Alt+F', category: 'Navigation', path: '/dossiers' },
  { id: 'notifications', label: 'Notifications & Alertes', description: 'Accéder aux notifications', defaultKey: 'Alt+N', category: 'Navigation', path: '/notifications-alertes' },
  { id: 'search', label: 'Recherche globale', description: 'Ouvrir la recherche globale', defaultKey: 'Alt+S', category: 'Navigation', action: 'search' },
  { id: 'parametres', label: 'Paramètres', description: 'Accéder aux paramètres', defaultKey: 'Alt+P', category: 'Administration', adminOnly: true, path: '/parametres' },
  { id: 'help', label: 'Aide raccourcis', description: 'Afficher la liste des raccourcis', defaultKey: 'Alt+?', category: 'Aide', action: 'help' },
];

export function getCustomShortcuts(userId: string): Record<string, string> {
  try {
    const stored = localStorage.getItem(`shortcuts_${userId}`);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function saveCustomShortcuts(userId: string, shortcuts: Record<string, string>): void {
  localStorage.setItem(`shortcuts_${userId}`, JSON.stringify(shortcuts));
}

export function getEffectiveKey(shortcutId: string, userId: string): string {
  const custom = getCustomShortcuts(userId);
  const def = DEFAULT_SHORTCUTS.find(s => s.id === shortcutId);
  return custom[shortcutId] || def?.defaultKey || '';
}

function parseKey(keyStr: string): { alt: boolean; ctrl: boolean; shift: boolean; key: string } {
  const parts = keyStr.split('+');
  return {
    alt: parts.includes('Alt'),
    ctrl: parts.includes('Ctrl'),
    shift: parts.includes('Shift'),
    key: parts[parts.length - 1],
  };
}

function matchesEvent(e: KeyboardEvent, keyStr: string): boolean {
  const parsed = parseKey(keyStr);
  const eventKey = e.key === '?' ? '?' : e.key;
  return (
    e.altKey === parsed.alt &&
    e.ctrlKey === parsed.ctrl &&
    e.shiftKey === parsed.shift &&
    eventKey.toLowerCase() === parsed.key.toLowerCase()
  );
}

interface UseKeyboardShortcutsOptions {
  onOpenSearch?: () => void;
  onOpenHelp?: () => void;
}

export function useKeyboardShortcuts({ onOpenSearch, onOpenHelp }: UseKeyboardShortcutsOptions = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!user?.id) return;
    const customKeys = getCustomShortcuts(user.id);

    for (const shortcut of DEFAULT_SHORTCUTS) {
      if (shortcut.adminOnly && user.role !== 'admin') continue;
      const effectiveKey = customKeys[shortcut.id] || shortcut.defaultKey;
      if (matchesEvent(e, effectiveKey)) {
        e.preventDefault();
        if (shortcut.action === 'search') {
          onOpenSearch?.();
        } else if (shortcut.action === 'help') {
          onOpenHelp?.();
        } else if (shortcut.path) {
          navigate(shortcut.path);
          if (shortcut.id === 'parametres') {
            window.dispatchEvent(new CustomEvent('shortcut-toast', { detail: { message: 'Navigation vers Paramètres (Alt+P)' } }));
          }
        }
        return;
      }
    }
  }, [user, navigate, onOpenSearch, onOpenHelp]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
